import { forwardRef, useCallback, useState } from 'react';
import {
  faCaretDown,
  faCaretRight,
  faPen,
  faPlus,
  faPaste,
  faFolderPlus,
  faFolderOpen,
  faFolderTree,
} from '@fortawesome/free-solid-svg-icons';
import { faCopy, faTrashAlt } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Workspace } from '@workspace/domain/workspace';
import type { Folder } from '@workspace/view-model-folder/domain/Folder';
import { SidebarMenuItem } from './SidebarMenuItem';
import { useCreateViewModelFromClipboardPayloadHandler } from '@workspace/view-model-folder/hooks';
import { ViewModelCreateModal } from '@workspace/view-model-folder/ui/ViewModelCreateModal';
import { FolderEditModal } from '@workspace/view-model-folder/ui/FolderEditModal';
import { SidebarMenuButton } from './SidebarMenuButton';
import { FolderDeleteModal } from '@workspace/view-model-folder/ui/FolderDeleteModal';
import { ViewModelOperation } from '@view-model/domain/view-model';
import { useActionLogSender } from '@framework/action-log';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import { toast } from 'react-hot-toast';
import { useCurrentUserId } from '@framework/auth';
import { FolderCreateModal } from '@workspace/view-model-folder/ui/FolderCreateModal';
import { MoveFolderModal } from '@workspace/view-model-folder/ui/MoveModal/MoveFolderModal';
import { MoveToOtherWorkspaceModal } from '@workspace/view-model-folder/ui/MoveToOtherWorkspaceModal';
import { useSidebarItemMenuPermissions } from './useSidebarItemMenuPermissions';
import { FolderItemTitle } from './FolderItemTitle';

type Modals =
  | 'ViewModelCreate'
  | 'FolderCreate'
  | 'FolderEdit'
  | 'FolderDelete'
  | 'MoveFolder'
  | 'MoveToOtherWorkspace';

const useHandleModals = () => {
  const [openModal, setOpenModal] = useState<Modals | null>(null);

  return {
    openModal,
    handleViewModelCreateModalOpen: useCallback(() => {
      setOpenModal('ViewModelCreate');
    }, []),
    handleFolderCreateModalOpen: useCallback(() => {
      setOpenModal('FolderCreate');
    }, []),
    handleFolderEditModalOpen: useCallback(() => {
      setOpenModal('FolderEdit');
    }, []),
    handleFolderDeleteModalOpen: useCallback(() => {
      setOpenModal('FolderDelete');
    }, []),
    handleMoveFolderModalOpen: useCallback(() => {
      setOpenModal('MoveFolder');
    }, []),
    handleMoveToOtherWorkspaceModalOpen: useCallback(() => {
      setOpenModal('MoveToOtherWorkspace');
    }, []),
    handleModalClose: useCallback(() => {
      setOpenModal(null);
    }, []),
  };
};

type Props = {
  workspace: Workspace;
  folder: Folder;
  folderTree: RootFolderTree;
  toggleFolder: () => void;
  isOpen: boolean;
  isSelected: boolean;
  firstLoaded?: boolean;
  searchKeyword?: string;
  children?: React.ReactNode;
};

export const FolderItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      workspace,
      folder,
      folderTree,
      toggleFolder,
      isOpen,
      isSelected,
      firstLoaded = true,
      searchKeyword,
      children,
    }: Props,
    ref
  ) => {
    const actionLogSender = useActionLogSender();

    const { isEditMenuButtonShowable } = useSidebarItemMenuPermissions(workspace);

    const {
      openModal,
      handleViewModelCreateModalOpen,
      handleFolderCreateModalOpen,
      handleFolderEditModalOpen,
      handleFolderDeleteModalOpen,
      handleMoveFolderModalOpen,
      handleMoveToOtherWorkspaceModalOpen,
      handleModalClose,
    } = useHandleModals();

    const currentUserId = useCurrentUserId();

    const handleDuplicateFolder = useCallback(async () => {
      if (!currentUserId) return;

      try {
        const onError = (message: string) => toast.error(message);
        toast('フォルダを複製中です...', { id: folder.id, duration: Infinity });
        await ViewModelOperation.duplicateFolder(folder.id, currentUserId, onError, actionLogSender);
        toast.success('フォルダを複製しました | Duplicate Succeeded');
      } catch (e) {
        toast.error('フォルダの複製に失敗しました | Duplicate Failed');
        throw e;
      } finally {
        toast.dismiss(folder.id);
      }
    }, [currentUserId, folder.id, actionLogSender]);

    const handleFolderSubmit = useCallback(
      async (name: string) => {
        await ViewModelOperation.create(workspace.id, folder.id, name, actionLogSender);
        handleModalClose();
      },
      [workspace.id, folder.id, actionLogSender, handleModalClose]
    );

    const createViewModelFromClipboardPayload = useCreateViewModelFromClipboardPayloadHandler(
      workspace.groupId,
      workspace.id,
      folder.id
    );

    return (
      <div className="relative" ref={ref}>
        <div className="flex items-center">
          <button className="mr-1 focus:outline-none" onClick={toggleFolder}>
            <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} className="size-4" />
          </button>
          <SidebarMenuItem
            isSelected={isSelected}
            isMenuButtonShowable={isEditMenuButtonShowable}
            popupMenu={
              <>
                <SidebarMenuButton
                  handleClick={handleViewModelCreateModalOpen}
                  icon={faPlus}
                  title={'新規ビューモデル'}
                />
                <SidebarMenuButton
                  handleClick={handleFolderCreateModalOpen}
                  icon={faFolderPlus}
                  title={'新規フォルダ'}
                />
                <SidebarMenuButton handleClick={handleDuplicateFolder} icon={faCopy} title={'フォルダを複製'} />
                <SidebarMenuButton
                  handleClick={createViewModelFromClipboardPayload}
                  icon={faPaste}
                  title={'ビューモデルをペースト'}
                />
                <SidebarMenuButton
                  handleClick={handleMoveFolderModalOpen}
                  icon={faFolderOpen}
                  title={'フォルダを移動'}
                />
                <SidebarMenuButton
                  handleClick={handleMoveToOtherWorkspaceModalOpen}
                  icon={faFolderTree}
                  title={'別のワークスペースに移動'}
                />
                <SidebarMenuButton handleClick={handleFolderEditModalOpen} icon={faPen} title={'編集'} />
                <SidebarMenuButton
                  handleClick={handleFolderDeleteModalOpen}
                  icon={faTrashAlt}
                  title={'削除'}
                  className="text-red-700"
                />
              </>
            }
          >
            <FolderItemTitle workspace={workspace} folder={folder} searchKeyword={searchKeyword} />
          </SidebarMenuItem>
        </div>

        {openModal === 'ViewModelCreate' && (
          <ViewModelCreateModal
            isOpen={openModal === 'ViewModelCreate'}
            onSubmit={handleFolderSubmit}
            onClose={handleModalClose}
          />
        )}

        {openModal === 'FolderCreate' && (
          <FolderCreateModal
            isOpen={openModal === 'FolderCreate'}
            onClose={handleModalClose}
            workspaceId={workspace.id}
            folderId={folder.id}
          />
        )}

        {openModal === 'FolderEdit' && (
          <FolderEditModal folder={folder} isOpen={openModal === 'FolderEdit'} onClose={handleModalClose} />
        )}

        {openModal === 'FolderDelete' && (
          <FolderDeleteModal
            folder={folder}
            isOpen={openModal === 'FolderDelete'}
            onClose={handleModalClose}
            hasChildren={folderTree.hasChildrenOf(folder.id)}
          />
        )}

        {openModal === 'MoveFolder' && (
          <MoveFolderModal isOpen={openModal === 'MoveFolder'} onClose={handleModalClose} folder={folder} />
        )}

        {currentUserId && openModal === 'MoveToOtherWorkspace' && (
          <MoveToOtherWorkspaceModal
            isOpen={openModal === 'MoveToOtherWorkspace'}
            onClose={handleModalClose}
            folder={folder}
            userId={currentUserId}
            groupId={workspace.groupId}
            workspaceId={workspace.id}
          />
        )}

        {isOpen && firstLoaded && <div className="ml-4">{children}</div>}
      </div>
    );
  }
);

FolderItem.displayName = 'FolderItem';
